import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcFundingV1SourceQueryServiceSourceGet } from '@interxfi/api-client/generated/hooks/ixc-funding-v1-source-query-service-source-get';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import { FundingSourceEntityMenu } from '../../../../../../../../../../../features/funding/source/funding-source-entity-menu';
import { FundingSourceEventSummary } from '../../../../../../../../../../../features/funding/source/funding-source-event';
import { getFundingSourceDetailPageListItems } from '../../../../../../../../../../../features/funding/source/funding-source-list-items';
import { getFundingSourceDetailPath } from '../../../../../../../../../../../features/funding/source/funding-source-routing';
import { FundingSourceStatusPill } from '../../../../../../../../../../../features/funding/source/funding-source-status-pill';
import { FundingSourceSummaryBar } from '../../../../../../../../../../../features/funding/source/summary-bar/funding-source-summary-bar';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/funding/_funding/source/$sourceId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-funding-source-${params.sourceId}`,
        label: 'Funding Source',
        to: getFundingSourceDetailPath(params.sourceId, params.accountId),
        suffix: <CopyButton content={params.sourceId} label="Copy Source ID" tooltip={{ copy: params.sourceId, copied: 'Copied Source ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Funding Source'),
  component: AccountFundingSourceDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load funding source', 'view this funding source');

function AccountFundingSourceDetail() {
  const { accountId, sourceId } = Route.useParams();
  const request = { sourceId } as const;
  const { data, error, isPending } = useIxcFundingV1SourceQueryServiceSourceGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getFundingSourceDetailPageListItems(data?.source), [data?.source]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={<FundingSourceEntityMenu sourceId={sourceId} useCase="account-detail" />}
          pills={<FundingSourceStatusPill isPending={showPending} size="large" status={data?.source?.status} />}
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <FundingSourceSummaryBar sourceId={sourceId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FundingSourceEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
